import type { Layout } from '../data/types';
import type { AABB, Ring, Vec2 } from '../geo/types';
import { createGroundElevation } from './groundElevation';
import { foundationHeight } from '../world/fabric/foundation';
import { CLOISTER_FLOOR } from './cloisterLevels';
import { centroid, pointInRing, ringBounds } from '../geo/polygon';
import { floorCandidates } from './floorCandidates';
import minderbroeders from '../world/fabric/landmarks/minderbroedersPlan.json';
import { oudeNonnenFloors } from '../world/fabric/landmarks/oudeNonnenPlan';
import { nieuweNonnenFloors } from '../world/fabric/landmarks/nieuweNonnenPlan';
import { sintMariaFloors } from '../world/fabric/landmarks/sintMariaPlan';
import { cellebroedersFloors } from '../world/fabric/landmarks/cellebroedersPlan';
import { sintClaraFloors } from '../world/fabric/landmarks/sintClaraPlan';
import { sintMargrietFloors } from '../world/fabric/landmarks/sintMargrietPlan';
import { sintGeertruidFloors } from '../world/fabric/landmarks/sintGeertruidPlan';
import { kartuizersFloors } from '../world/fabric/landmarks/kartuizersPlan';
import { sintLucienFloors } from '../world/fabric/landmarks/sintLucienPlan';
import { heiligewegClarissenFloors } from '../world/fabric/landmarks/heiligewegClarissenPlan';
import {
  hasMagdalenaBarbaraPlan,
  magdalenaBarbaraFloors,
} from '../world/fabric/landmarks/magdalenaBarbaraPlan';
import { cellezustersFloors } from '../world/fabric/landmarks/cellezustersPlan';
import {
  prinsenhofEntryHeight,
  prinsenhofFloors,
  prinsenhofFrame,
  prinsenhofPartFrame,
} from '../world/fabric/landmarks/prinsenhofConventsPlan';
import {
  ursulaEntryHeight,
  ursulaFloors,
  ursulaFrame,
  ursulaPartFrame,
} from '../world/fabric/landmarks/ursulaPlan';

export { CLOISTER_FLOOR } from './cloisterLevels';

function terrainCourtsOf(layout: Layout) {
  const elevation = createGroundElevation(layout);
  return layout.landmarks.flatMap((landmark) => {
    const floors =
      landmark.id === 'landmark-kartuizerklooster'
        ? kartuizersFloors(landmark)
        : landmark.id === 'landmark-clarissen'
          ? heiligewegClarissenFloors(landmark)
          : landmark.id === 'landmark-sint-lucien'
            ? sintLucienFloors(landmark)
            : landmark.id === 'landmark-sint-geertruid'
              ? sintGeertruidFloors(landmark)
              : landmark.id === 'landmark-sint-margriet'
                ? sintMargrietFloors(landmark)
                : landmark.id === 'landmark-sint-clara'
                  ? sintClaraFloors(landmark)
                  : hasMagdalenaBarbaraPlan(landmark)
                    ? magdalenaBarbaraFloors(landmark)
                    : landmark.id === 'landmark-oude-nonnen'
                      ? oudeNonnenFloors(landmark)
                      : landmark.id === 'landmark-cellebroeders'
                        ? cellebroedersFloors(landmark)
                        : landmark.id === 'landmark-sint-maria'
                          ? sintMariaFloors(landmark)
                          : landmark.id === 'landmark-nieuwe-nonnen'
                            ? nieuweNonnenFloors(landmark)
                            : [];
    return floors.map((f) => ({ ...f, bounds: ringBounds(f.ring), elevation }));
  });
}

function ursulaFloorsOf(layout: Layout) {
  const l = layout.landmarks.find((l) => l.id === 'landmark-sint-ursula');
  if (!l) return [];
  const elevation = createGroundElevation(layout);
  return [
    {
      ...ursulaFloors(l),
      plan: ursulaFrame(l),
      base: foundationHeight(l.footprint, elevation),
      elevation,
      bounds: ringBounds(l.footprint),
    },
  ];
}

function prinsenhofFloorsOf(layout: Layout) {
  const elevation = createGroundElevation(layout);
  return layout.landmarks
    .filter((l) => ['landmark-sint-cecilia', 'landmark-sint-catharina'].includes(l.id))
    .map((l) => ({
      ...prinsenhofFloors(l),
      bounds: ringBounds(l.footprint),
      plan: prinsenhofFrame(l),
      base: foundationHeight(l.footprint, elevation),
      elevation,
    }));
}

function celleFloorsOf(layout: Layout) {
  const l = layout.landmarks.find((l) => l.id === 'landmark-cellezusters');
  if (!l) return [];
  const elevation = createGroundElevation(layout),
    base = foundationHeight(l.footprint, elevation);
  return cellezustersFloors(l).map((f) => ({ ...f, base, bounds: ringBounds(f.ring) }));
}

export function friaryGardenRects() {
  const b = minderbroeders.beds,
    beds = [];
  for (let row = 0; row < b.rows; row++)
    for (let col = 0; col < b.columns; col++) {
      const du = (b.u1 - b.u0) / b.columns,
        dv = (b.v1 - b.v0) / b.rows;
      beds.push({
        rect: {
          u0: b.u0 + col * du + 0.4,
          u1: b.u0 + (col + 1) * du - 0.4,
          v0: b.v0 + row * dv + 0.4,
          v1: b.v0 + (row + 1) * dv - 0.4,
        },
        height: 0.035,
      });
    }
  return [
    { rect: minderbroeders.orchard, height: 0.03 },
    { rect: minderbroeders.cemetery, height: 0.03 },
    ...beds,
  ];
}

/** The private approach rises from the quay-side ground to the levelled west walk. */
export function friaryEntryHeight(u: number, start: number, walk: number): number {
  const t = Math.max(
    0,
    Math.min(
      1,
      (u - minderbroeders.entry.u0) / (minderbroeders.ranges[1]!.u0 - minderbroeders.entry.u0)
    )
  );
  return start + (walk - start) * t;
}

/** The refectory/cellar passage meets the lower orchard without an abrupt foundation step. */
export function friaryGardenEntryHeight(v: number, start: number, walk: number): number {
  const t = Math.max(
    0,
    Math.min(
      1,
      (v - minderbroeders.gardenEntry.v0) /
        (minderbroeders.rooms[0]!.v0 - minderbroeders.gardenEntry.v0)
    )
  );
  return start + (walk - start) * t;
}

function friaryFloorsOf(layout: Layout) {
  const landmark = layout.landmarks.find((l) => l.id === 'landmark-minderbroeders');
  if (!landmark) return [];
  const x = landmark.params['anchorX'],
    y = landmark.params['anchorY'];
  if (typeof x !== 'number' || typeof y !== 'number') return [];
  const elevation = createGroundElevation(layout),
    base = foundationHeight(landmark.footprint, elevation),
    cos = Math.cos(landmark.heading),
    sin = Math.sin(landmark.heading);
  return [
    { rect: minderbroeders.court, height: CLOISTER_FLOOR.garden },
    ...minderbroeders.walks.map((rect) => ({ rect, height: CLOISTER_FLOOR.walk })),
    { rect: minderbroeders.entry, height: 0.03, entry: true },
    { rect: minderbroeders.gardenEntry, height: 0.03, gardenEntry: true },
    ...friaryGardenRects().map((r) => ({ ...r, terrain: true })),
  ].map((r) => ({
    ...r,
    x,
    y,
    cos,
    sin,
    base,
    elevation,
    bounds: ringBounds(
      [
        [r.rect.u0, r.rect.v0],
        [r.rect.u1, r.rect.v0],
        [r.rect.u1, r.rect.v1],
        [r.rect.u0, r.rect.v1],
      ].map(([u, v]): Vec2 => [x + u! * cos - v! * sin, y + u! * sin + v! * cos])
    ),
  }));
}

/** The placed cloister's levelled floor, shared by geometry and walking. */
function courtsOf(layout: Layout) {
  const elevation = createGroundElevation(layout);
  return layout.landmarks.flatMap((landmark) => {
    const p = landmark.params;
    if (
      landmark.kind !== 'monastery' ||
      typeof p['garth'] !== 'number' ||
      landmark.id === 'landmark-clarissen'
    )
      return [];
    const origin = centroid(landmark.footprint),
      cos = Math.cos(landmark.heading),
      sin = Math.sin(landmark.heading),
      u = typeof p['cloisterU'] === 'number' ? p['cloisterU'] : 0,
      v = typeof p['cloisterV'] === 'number' ? p['cloisterV'] : 0,
      half = p['garth'] / 2,
      range = typeof p['range'] === 'number' ? p['range'] : 9,
      span = (half + range) * (Math.abs(cos) + Math.abs(sin)),
      x = origin[0] + u * cos - v * sin,
      y = origin[1] + u * sin + v * cos;
    return [
      {
        base: foundationHeight(landmark.footprint, elevation),
        origin,
        cos,
        sin,
        u,
        v,
        half,
        range,
        bounds: { minX: x - span, maxX: x + span, minY: y - span, maxY: y + span },
      },
    ];
  });
}

function withinBounds(p: Vec2, b: AABB): boolean {
  return p[0] >= b.minX && p[0] <= b.maxX && p[1] >= b.minY && p[1] <= b.maxY;
}

/** The physically built courts and walks, whose own levelled slabs replace street paving. */
export function cloisterFloorFootprints(layout: Layout): readonly Ring[] {
  const generic = courtsOf(layout).map((court) => {
    const half = court.half + court.range;
    return [
      [-half, -half],
      [half, -half],
      [half, half],
      [-half, half],
    ].map(([u, v]): Vec2 => [
      court.origin[0] + (u! + court.u) * court.cos - (v! + court.v) * court.sin,
      court.origin[1] + (u! + court.u) * court.sin + (v! + court.v) * court.cos,
    ]);
  });
  return [
    ...generic,
    ...terrainCourtsOf(layout).map((f) => f.ring),
    ...ursulaFloorsOf(layout).flatMap((f) => [
      f.court.ring,
      ...f.beds.map((b) => b.ring),
      f.entry.ring,
    ]),
    ...prinsenhofFloorsOf(layout).flatMap((f) => [
      f.court.ring,
      ...f.beds.map((b) => b.ring),
      f.entry.ring,
    ]),
    ...celleFloorsOf(layout).map((f) => f.ring),
    ...friaryFloorsOf(layout).map((f) => {
      const r = f.rect;
      return [
        [r.u0, r.v0],
        [r.u1, r.v0],
        [r.u1, r.v1],
        [r.u0, r.v1],
      ].map(([u, v]): Vec2 => [f.x + u! * f.cos - v! * f.sin, f.y + u! * f.sin + v! * f.cos]);
    }),
  ];
}

export function createCloisterFloor(layout: Layout): (p: Vec2) => number | null {
  const courts = floorCandidates(courtsOf(layout));
  const terrainCourts = floorCandidates(terrainCourtsOf(layout).reverse());
  const friary = floorCandidates(friaryFloorsOf(layout));
  const celle = floorCandidates(celleFloorsOf(layout));
  const prinsenhof = floorCandidates(prinsenhofFloorsOf(layout));
  const ursula = floorCandidates(ursulaFloorsOf(layout));
  return (p) => {
    // Beds sit above the surrounding earth; check them before the overlapping court patches.
    for (const f of terrainCourts(p))
      if (withinBounds(p, f.bounds) && pointInRing(p, f.ring)) return f.elevation(p) + f.height;
    for (const f of ursula(p)) {
      // Most terrain samples lie outside this compound; avoid checking every bed for them.
      if (!withinBounds(p, f.bounds)) continue;
      if (pointInRing(p, f.entry.ring))
        return ursulaEntryHeight(
          ursulaPartFrame(f.plan, f.entry.part),
          f.entry.part,
          p,
          f.elevation,
          f.base
        );
      for (const bed of f.beds) if (pointInRing(p, bed.ring)) return f.base + bed.height;
      if (pointInRing(p, f.court.ring)) return f.base + f.court.height;
    }
    for (const f of prinsenhof(p)) {
      if (!withinBounds(p, f.bounds)) continue;
      if (pointInRing(p, f.entry.ring))
        return prinsenhofEntryHeight(
          prinsenhofPartFrame(f.plan, f.entry.part),
          f.entry.part,
          p,
          f.elevation,
          f.base
        );
      for (const bed of f.beds) if (pointInRing(p, bed.ring)) return f.base + bed.height;
      if (pointInRing(p, f.court.ring)) return f.base + f.court.height;
    }
    for (const f of celle(p))
      if (withinBounds(p, f.bounds) && pointInRing(p, f.ring)) return f.base + f.height;
    for (const f of friary(p)) {
      const dx = p[0] - f.x,
        dy = p[1] - f.y,
        u = dx * f.cos + dy * f.sin,
        v = -dx * f.sin + dy * f.cos;
      if (u >= f.rect.u0 && u <= f.rect.u1 && v >= f.rect.v0 && v <= f.rect.v1)
        if ('terrain' in f) return f.elevation(p) + f.height;
        else if ('gardenEntry' in f) {
          const entry = minderbroeders.gardenEntry,
            mid = (entry.u0 + entry.u1) / 2,
            start: Vec2 = [
              f.x + mid * f.cos - entry.v0 * f.sin,
              f.y + mid * f.sin + entry.v0 * f.cos,
            ];
          return friaryGardenEntryHeight(
            v,
            f.elevation(start) + 0.03,
            f.base + CLOISTER_FLOOR.walk
          );
        } else if ('entry' in f) {
          const entry = minderbroeders.entry,
            mid = (entry.v0 + entry.v1) / 2,
            start: Vec2 = [
              f.x + entry.u0 * f.cos - mid * f.sin,
              f.y + entry.u0 * f.sin + mid * f.cos,
            ];
          return friaryEntryHeight(u, f.elevation(start) + 0.03, f.base + CLOISTER_FLOOR.walk);
        } else return f.base + f.height;
    }
    for (const court of courts(p)) {
      const dx = p[0] - court.origin[0],
        dy = p[1] - court.origin[1];
      const u = Math.abs(dx * court.cos + dy * court.sin - court.u);
      const v = Math.abs(-dx * court.sin + dy * court.cos - court.v);
      if (Math.max(u, v) <= court.half) return court.base + CLOISTER_FLOOR.garden;
      if (Math.max(u, v) <= court.half + court.range) return court.base + CLOISTER_FLOOR.walk;
    }
    return null;
  };
}
